import { IAlarm, IAttachment } from '@obvious.tech/constellation'
import { Result } from 'neverthrow'
import { useEffect } from 'react'

import api, { TApiResponse, TEntity, TUseApiActionState } from 'src/utils/api'

const getDataURLByAttachmentID = ({
  id,
  token,
}: {
  id: string
  token?: string
}) => {
  return api
    .getResourceBlobAsDataURL(`alarms/api/Attachment/${id}/download`)({ token })
    .mapErr(
      error =>
        new Error(
          `Failed to fetch Attachment ${id}: ` +
            `${error.toString() ?? 'An unexpected error occured'}`,
          { cause: error },
        ),
    )
}

export type TAttachmentWithURI = TEntity<IAttachment> & {
  dataURI: Result<string, Error> | null
}

const getAttachmentsByAlarmID = async ({
  id,
  token,
}: {
  id: string
  token?: string
}): Promise<TApiResponse<TAttachmentWithURI[]>> => {
  const attachments = await api.fetchResource<Array<TEntity<IAttachment>>>(
    `alarms/api/Alarm/${id}/Attachment`,
  )({ token })

  if (attachments._tag === 'failure') {
    return {
      _tag: 'failure',
      error: attachments.error,
    }
  }

  const attachmentsWithURIs = await Promise.all(
    attachments.data.map(async attachment => {
      return Object.assign(attachment, {
        dataURI:
          attachment.file != null
            ? await getDataURLByAttachmentID({ id: attachment.id, token })
            : null,
      })
    }),
  )

  return {
    _tag: 'success',
    data: attachmentsWithURIs,
  }
}

export type TProps = {
  alarm: TEntity<IAlarm>
  authorization?: string
}

export type TUseAttachmentsResult = TUseApiActionState<
  typeof getAttachmentsByAlarmID
>

const useAttachments = ({ alarm, authorization }: TProps) => {
  const [attachments, getAttachments] = api.useApiAction(
    getAttachmentsByAlarmID,
  )

  useEffect(() => {
    return getAttachments({
      id: alarm.id,
      token: authorization,
    })
  }, [alarm, authorization, getAttachments])

  return attachments
}

export default useAttachments
